import React from "react";
import { Link } from "react-router-dom";
import { FaClock, FaEye, FaLaptop, FaMoon } from "react-icons/fa";

const featuredInsights = [
  {
    title: "Reduce Digital Eye Strain in 7 Daily Steps",
    category: "Screen Care",
    tone: "from-blue-50 to-slate-50",
    image:
      "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=1000&q=80",
  },
  {
    title: "Nutrition for Better Vision: Lutein, Omega-3, and Zinc",
    category: "Eye Nutrition",
    tone: "from-indigo-50 to-purple-50",
    image:
      "https://images.unsplash.com/photo-1494390248081-4e521a5940db?auto=format&fit=crop&w=1000&q=80",
  },
  {
    title: "Sleep, Light, and Vision Recovery: Night Routine Guide",
    category: "Recovery",
    tone: "from-sky-50 to-amber-50",
    image:
      "https://images.unsplash.com/photo-1527443154391-507e9dc6c5cc?auto=format&fit=crop&w=1000&q=80",
  },
];

const keyStats = [
  {
    label: "Users Improved Comfort",
    value: "92%",
    detail: "Reported less eye fatigue in 30 days",
  },
  {
    label: "Daily Screen Break Plans",
    value: "12K+",
    detail: "Personalized routines generated",
  },
  {
    label: "Average Session Time",
    value: "8m",
    detail: "Quick, practical eye-care guidance",
  },
  {
    label: "Specialist Contributors",
    value: "40+",
    detail: "Optometrists and wellness coaches",
  },
];

const services = [
  {
    title: "Eye Strain Risk Check",
    description: "Assess posture, lighting, blink rate, and screen habits.",
    icon: FaEye,
  },
  {
    title: "20-20-20 Smart Reminders",
    description: "Scheduled nudges to rest eyes and reset focus.",
    icon: FaClock,
  },
  {
    title: "Vision-Friendly Workspace Guide",
    description: "Optimize contrast, brightness, and monitor distance.",
    icon: FaLaptop,
  },
  {
    title: "Sleep & Blue-Light Coaching",
    description: "Better evening routines for eye recovery and sleep quality.",
    icon: FaMoon,
  },
];

const recommended = [
  {
    title: "Best monitor settings to reduce dry eyes",
    image:
      "https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=900&q=80",
  },
  {
    title: "How often should you blink while working?",
    image:
      "https://images.unsplash.com/photo-1516117172878-fd2c41f4a759?auto=format&fit=crop&w=900&q=80",
  },
  {
    title: "Top foods that support retinal health",
    image:
      "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&w=900&q=80",
  },
  {
    title: "Desk stretches that relax neck and visual focus",
    image:
      "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?auto=format&fit=crop&w=900&q=80",
  },
  {
    title: "Night mode: when to enable and when not to",
    image:
      "https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?auto=format&fit=crop&w=900&q=80",
  },
  {
    title: "Children and screen time: healthy visual habits",
    image:
      "https://images.unsplash.com/photo-1516627145497-ae6968895b74?auto=format&fit=crop&w=900&q=80",
  },
];

const symptoms = [
  "Dry or watery eyes",
  "Blurred vision after long screen use",
  "Frequent headaches and eye pressure",
  "Light sensitivity in evening hours",
  "Difficulty focusing between near and far",
  "Neck and shoulder tension from poor posture",
];

const testimonials = [
  {
    name: "Nina K.",
    role: "Product Designer",
    quote:
      "The daily break planner helped me reduce burning-eye episodes within two weeks.",
  },
  {
    name: "Arman R.",
    role: "Software Engineer",
    quote:
      "I finally fixed my monitor and lighting setup. My evening headaches dropped a lot.",
  },
];

const faqs = [
  {
    q: "How often should I take screen breaks?",
    a: "Use the 20-20-20 rule: every 20 minutes, look 20 feet away for 20 seconds.",
  },
  {
    q: "Can nutrition really affect eye health?",
    a: "Yes. Lutein, omega-3s, vitamin A, and zinc support long-term eye function.",
  },
  {
    q: "Is dark mode always better for eyes?",
    a: "Not always. It depends on ambient light, contrast, and your individual comfort.",
  },
];

const HomePage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-blue-50 text-slate-800">
      <header className="bg-gradient-to-r from-[#1e5782] via-[#315f98] to-[#5478b8] text-blue-50 px-5 py-14">
        <div className="mx-auto w-full max-w-6xl">
          <p className="mb-2 text-xs uppercase tracking-[0.08em] opacity-90">
            Welcome to Eye Wellness
          </p>
          <h1 className="mb-3 text-4xl font-semibold leading-tight md:text-5xl">
            Professional Eye Wellness for the Digital Age
          </h1>
          <p className="max-w-2xl text-base leading-7 opacity-95">
            Personalized eye-care guidance, prevention strategies, and evidence-based
            habits to protect your vision while working, studying, and scrolling.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/login"
              className="rounded-full bg-white px-5 py-2.5 font-semibold text-[#1e5782] transition hover:bg-blue-100"
            >
              Log In
            </Link>
            <Link
              to="/register"
              className="rounded-full border border-white/60 px-5 py-2.5 font-semibold text-white transition hover:bg-white/15"
            >
              Join Free
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto -mt-8 w-full max-w-6xl px-5 pb-14">
        <section className="mb-7">
          <div className="grid grid-cols-2 gap-3 rounded-2xl border border-blue-100 bg-white p-4 shadow-sm md:grid-cols-4">
            {keyStats.map((item) => (
              <article key={item.label} className="rounded-xl bg-slate-50 p-3 text-left">
                <p className="text-2xl font-bold text-[#1e5782]">{item.value}</p>
                <p className="text-sm font-semibold text-slate-800">{item.label}</p>
                <p className="text-xs text-slate-600">{item.detail}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="mb-7">
          <h2 className="mb-2.5 font-serif text-3xl text-[#1f3557] md:text-[32px]">
            Featured Eye Wellness Insights
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {featuredInsights.map((item) => (
              <article
                className={`rounded-2xl border border-slate-200 bg-gradient-to-b ${item.tone} p-3.5 text-left shadow-[0_10px_26px_rgba(20,45,79,0.10)]`}
                key={item.title}
              >
                <img
                  src={item.image}
                  alt={item.title}
                  className="mb-3 h-32 w-full rounded-lg object-cover"
                />
                <span className="inline-block rounded-full bg-slate-800/10 px-2.5 py-1 text-xs font-semibold">
                  {item.category}
                </span>
                <h3 className="mt-3 text-[19px] leading-[1.35] text-slate-800">
                  {item.title}
                </h3>
              </article>
            ))}
          </div>
        </section>

        <section className="mb-7">
          <h2 className="mb-2.5 font-serif text-3xl text-[#1f3557] md:text-[32px]">
            What You Can Do Here
          </h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {services.map((item) => (
              <article
                key={item.title}
                className="rounded-2xl border border-blue-100 bg-white p-5 text-left shadow-[0_10px_26px_rgba(20,45,79,0.08)]"
              >
                <item.icon className="text-2xl text-[#1e5782]" aria-hidden="true" />
                <h3 className="mt-2 text-lg font-semibold text-slate-800">{item.title}</h3>
                <p className="mt-1 text-slate-600">{item.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="mb-7">
          <h2 className="mb-2.5 font-serif text-3xl text-[#1f3557] md:text-[32px]">
            Recommended for Eye Wellness
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
            {recommended.map((item) => (
              <article
                className="overflow-hidden rounded-xl border border-blue-100 bg-white text-left"
                key={item.title}
              >
                <img
                  src={item.image}
                  alt={item.title}
                  className="h-28 w-full object-cover"
                />
                <p className="m-0 px-3 py-3 text-sm leading-5 text-slate-700">
                  {item.title}
                </p>
              </article>
            ))}
          </div>
        </section>

        <section className="mb-7 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <article className="rounded-2xl border border-blue-100 bg-white p-5">
            <h2 className="mb-3 font-serif text-3xl text-[#1f3557] md:text-[32px]">
              Common Symptoms We Track
            </h2>
            <ul className="grid grid-cols-1 gap-2 text-slate-700">
              {symptoms.map((item) => (
                <li key={item} className="rounded-lg bg-slate-50 px-3 py-2">
                  {item}
                </li>
              ))}
            </ul>
          </article>
          <article className="rounded-2xl border border-blue-100 bg-white p-5">
            <h2 className="mb-3 font-serif text-3xl text-[#1f3557] md:text-[32px]">
              Real User Results
            </h2>
            <div className="space-y-3">
              {testimonials.map((item) => (
                <blockquote key={item.name} className="rounded-lg bg-slate-50 p-3">
                  <p className="text-slate-700">"{item.quote}"</p>
                  <footer className="mt-1 text-sm font-semibold text-[#1e5782]">
                    {item.name} - {item.role}
                  </footer>
                </blockquote>
              ))}
            </div>
          </article>
        </section>

        <section className="mb-7 rounded-2xl border border-blue-100 bg-white p-5">
          <h2 className="mb-3 font-serif text-3xl text-[#1f3557] md:text-[32px]">
            Frequently Asked Questions
          </h2>
          <div className="space-y-3">
            {faqs.map((item) => (
              <article key={item.q} className="rounded-lg border border-slate-200 p-3">
                <h3 className="font-semibold text-slate-800">{item.q}</h3>
                <p className="mt-1 text-slate-600">{item.a}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="rounded-2xl bg-gradient-to-r from-[#1e5782] via-[#315f98] to-[#5478b8] p-6 text-center text-white">
          <h2 className="text-2xl font-semibold md:text-3xl">
            Start Your Personalized Eye Wellness Plan
          </h2>
          <p className="mx-auto mt-2 max-w-2xl text-blue-50">
            Build healthier screen habits, track your symptoms, and protect your
            long-term vision with guided daily actions.
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/register"
              className="rounded-full bg-white px-5 py-2.5 font-semibold text-[#1e5782] transition hover:bg-blue-100"
            >
              Create Free Account
            </Link>
            <Link
              to="/login"
              className="rounded-full border border-white/60 px-5 py-2.5 font-semibold text-white transition hover:bg-white/15"
            >
              I Already Have an Account
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
};

export default HomePage;
